import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { partial, isNumber } from 'lodash';
import { userSettingsLabels } from '../UserSettingsForm/UserSettings';
import AccessCheckbox from './AccessCheckbox';
import SettingsGroup from './SettingsGroup';
import styles from './UserSettingsAndAccessForm.module.css';
import type {
  PermissionMap,
  UserSettings,
  UserOrganizationSettings,
  Persona,
  UserData,
} from 'admin/types';
import { BlockedByPersonaText } from 'admin/utils/shouldRender';
import { getMostRestrictiveValue } from 'admin/utils/getMostRestrictiveValue';
import { hasOrgFeatureOrSettings } from 'admin/utils/renderSettingConditions';
import { PermissionKey } from 'js-sdk/src/types/Persona';
import { mobxInjectSelect } from 'common/utils';
import { DotsIndicator } from 'common/components';
import { useAppSelector } from 'redux-stores';
import TCClient from 'client';

type BoolUserSettings = Omit<
  UserSettings,
  | 'id'
  | 'autoLogout'
  | 'personaId'
  | 'personaName'
  | 'personaMappingExclusion'
  | 'pinLock'
  | 'messageLifespan'
  | 'messageLifespanOptIn'
  | 'pinLockOptIn'
>;

type NumericUserSettings = Pick<UserSettings, 'pinLock' | 'messageLifespan'>;

type Settings = BoolUserSettings &
  NumericUserSettings & {
    messageLifespanOptIn?: boolean;
    pinLockOptIn?: boolean;
  };

type MobxProps = {
  featureFlags: Record<string, unknown>;
  findPersona: (props: {
    personaId: string;
    selectedOrganizationId: string;
  }) => Promise<Persona | undefined>;
  getURLParams: () => Record<string, string>;
  isPersonaMessageTtlAndPinLockEnabled: boolean;
  loadPermissions: (
    selectedOrganizationId: string
  ) => Promise<{ name: PermissionKey; description: string }[]>;
};

type UserSettingsAndAccessFormProps = {
  disableAll?: boolean;
  isPersonaForm?: boolean;
  userPermissions: PermissionMap | undefined;
  userSettings: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>;
  setUserPermissions: React.Dispatch<React.SetStateAction<PermissionMap | undefined>>;
  userData?: UserData;
};

const getUserPersonas = (userData: UserData, organizationId: string) => {
  const rawUserPersonas = userData?.organizationSettings.find(
    (org) => org.organizationId === organizationId
  );
  let userPersonas = null;
  if (rawUserPersonas?.permission_groups) {
    if (!Array.isArray(rawUserPersonas?.permission_groups)) {
      userPersonas = [rawUserPersonas?.permission_groups];
    } else {
      userPersonas = rawUserPersonas?.permission_groups;
    }
  }
  return userPersonas;
};

function UserSettingsAndAccessForm({
  disableAll = false,
  featureFlags,
  isPersonaForm = false,
  isPersonaMessageTtlAndPinLockEnabled,
  getURLParams,
  loadPermissions,
  userPermissions = {},
  userSettings,
  setUserSettings,
  setUserPermissions,
  userData,
}: UserSettingsAndAccessFormProps & MobxProps) {
  const [userOrgSettings, setUserOrgSettings] = useState<UserOrganizationSettings>();
  const [orgPermissions, setOrgPermissions] = useState<
    Map<PermissionKey, { name: string; description: string }>
  >(new Map());
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const organizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const urlParams = getURLParams();

  useEffect(() => {
    TCClient.adminUsers
      .fetchOrganizationSettings({ organizationId })
      .then((org_settings) => setUserOrgSettings(org_settings));
    loadPermissions(organizationId).then((permissions) =>
      setOrgPermissions(new Map(permissions.map((p) => [p.name, p])))
    );
  }, [loadPermissions, organizationId]);

  useEffect(() => {
    if (userPermissions && Object.keys(userPermissions).length > 0) {
      setPermissions(userPermissions);
    }
  }, [isPersonaForm, orgPermissions, userPermissions]);

  const { permissions: personasPermissions, settings: personasSettings } = useMemo(() => {
    const permissions = {} as Record<PermissionKey, boolean>;
    const settings: Settings = {} as Settings;
    if (userData) {
      const userPersonas = getUserPersonas(userData, organizationId);
      userPersonas?.forEach((persona) => {
        Object.keys(persona?.details?.permissions || {}).forEach((permission) => {
          if (!permissions[permission as PermissionKey]) {
            permissions[permission as PermissionKey] =
              persona?.details?.permissions?.[permission] || false;
          }
        });
        const personaSettings = persona?.details?.settings as unknown as Settings;
        if (personaSettings) {
          Object.keys(personaSettings).forEach((setting) => {
            if (!['pinLock', 'messageLifespan'].includes(setting)) {
              const key = setting as keyof BoolUserSettings;
              if (!settings[key]) {
                settings[key] = !!personaSettings[key];
              }
            } else {
              const key = setting as keyof NumericUserSettings;
              if (!personaSettings.messageLifespanOptIn && key === 'messageLifespan') return;
              if (!personaSettings.pinLockOptIn && key === 'pinLock') return;
              if (settings[key] === undefined) {
                settings[key] = personaSettings[key];
              } else {
                settings[key] = getMostRestrictiveValue(settings[key], personaSettings[key]);
              }
            }
          });
        }
      });
    }
    return { permissions, settings };
  }, [organizationId, userData]);
  const isLockedByPersona = useCallback(
    (type: 'permissions' | 'settings', title: string) => {
      if (!userData || !getUserPersonas(userData, organizationId)?.length) return false;
      if (userSettings.personaMappingExclusion) return false;
      if (type === 'permissions') {
        return !!personasPermissions?.[title as PermissionKey];
      } else {
        if (['pinLock', 'messageLifespan'].includes(title)) {
          return !isPersonaMessageTtlAndPinLockEnabled
            ? false
            : isNumber(personasSettings?.[title as keyof NumericUserSettings]);
        } else {
          return !!personasSettings?.[title as keyof BoolUserSettings];
        }
      }
    },
    [
      organizationId,
      personasPermissions,
      personasSettings,
      userData,
      userSettings.personaMappingExclusion,
      isPersonaMessageTtlAndPinLockEnabled,
    ]
  );

  if (!userSettings || !userOrgSettings) {
    return (
      <div className={classNames(styles.loadingHolder)}>
        <DotsIndicator color="#0e0d0d" size={12} marginTop={30} marginRight={5} />
      </div>
    );
  }

  const { settings, patientEngagementSettings } = userSettingsLabels(
    userSettings,
    userOrgSettings,
    setUserSettings,
    featureFlags,
    urlParams,
    disableAll,
    'v2',
    partial(isLockedByPersona, 'settings')
  );

  const settingsToRemove = isPersonaMessageTtlAndPinLockEnabled
    ? []
    : ['Message Lifespan', 'PIN Lock (mobile)'];

  const filteredSettings = isPersonaForm
    ? settings.filter((setting) => !settingsToRemove.includes(setting.label))
    : settings;

  return (
    <div className={styles.userSettingsAndAccessForm}>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <SettingsGroup
          isPersonaForm={isPersonaForm}
          title="General Settings"
          settings={filteredSettings}
          userOrgSettings={userOrgSettings}
        />
        {hasOrgFeatureOrSettings(userOrgSettings, ['featurePatientEngagement']) ? (
          <SettingsGroup
            isPersonaForm={isPersonaForm}
            title="Patient Engagement Settings"
            settings={patientEngagementSettings}
            userOrgSettings={userOrgSettings}
          />
        ) : null}
      </div>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupTitle}>Access</div>
        <div className={styles.settingsGroupGrid}>
          {Object.keys(permissions).map((permission) => {
            const lockedByPersona = isLockedByPersona('permissions', permission);
            return (
              <AccessCheckbox
                key={permission}
                isLockedByPersona={lockedByPersona}
                permission={permission}
                blockedByPersonaText={BlockedByPersonaText}
                orgPermissions={orgPermissions}
                disableAll={disableAll}
                checked={
                  permissions[permission as keyof PermissionMap] ||
                  personasPermissions[permission as PermissionKey]
                }
                onClick={(checked: boolean) =>
                  setUserPermissions((prev) => ({ ...prev, [permission]: checked }))
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default mobxInjectSelect<UserSettingsAndAccessFormProps, MobxProps>({
  featureStore: ['featureFlags', 'getURLParams'],
  messengerStore: ['isPersonaMessageTtlAndPinLockEnabled'],
  personaStore: ['findPersona', 'loadPermissions'],
})(UserSettingsAndAccessForm);
